import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FORM_OPTIONS } from "@/data/content";
import { Overline, CTAButton } from "@/components/ui-bits";
import { Reveal } from "@/components/motion-primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: "", company_name: "", email: "", phone: "", council_account_number: "",
  service_required: "", property_type: "", municipality: "", message: "",
};

const inputCls = "bg-slate-50 border-slate-200 rounded-lg h-12 focus-visible:ring-2 focus-visible:ring-secondary/40 focus-visible:border-secondary";

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSel = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.service_required || !form.message) {
      toast.error("Please complete all required fields marked with *");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      // Google Ads conversion: "Submit lead form" — fires ONLY on successful submission.
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "conversion", { send_to: "AW-787440125/8f7-CKqmwvAcEP3DvfcC" });
      }
      setDone(true);
      toast.success("Request received — we'll be in touch within 48 hours.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us on 081 560 7949.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="relative py-24 md:py-32 bg-background overflow-hidden">
      <div className="absolute top-0 left-0 w-1/3 h-full dot-grid opacity-[0.4]" />
      <div className="container mx-auto px-4 md:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left copy */}
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <Reveal><Overline>Request a Quote</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-primary leading-[1.1]">
                Let&rsquo;s sort out your municipal account.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-base text-muted-foreground leading-relaxed">
                Complete the Request for Quotation form and our team will contact you with a quotation within 48 hours.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 flex items-center gap-3 rounded-xl bg-muted/60 border border-slate-200 px-4 py-3.5">
                <ShieldCheck className="w-5 h-5 text-secondary shrink-0" />
                <span className="text-sm text-slate-600">Your details are used only to respond to your enquiry.</span>
              </div>
            </Reveal>
          </div>

          {/* Form / success */}
          <div className="lg:col-span-8">
            <div className="rounded-2xl border border-slate-200/80 bg-white shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] p-6 md:p-10">
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="text-center py-10" data-testid="quote-success"
                  >
                    <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-secondary/15 text-secondary">
                      <CheckCircle2 className="w-8 h-8" />
                    </div>
                    <h3 className="mt-6 font-heading text-2xl font-semibold text-primary">Thank you.</h3>
                    <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                      Your request has been received. A member of the SML Municipal Consulting team will contact you
                      regarding your enquiry.
                    </p>
                    <button
                      onClick={() => { setForm(EMPTY); setDone(false); }}
                      data-testid="quote-reset"
                      className="mt-8 text-sm font-semibold text-primary hover:text-secondary transition-colors"
                    >
                      Submit another request
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form" onSubmit={submit} data-testid="quote-form"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-5"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="full_name" className="text-sm font-medium text-slate-700">Full Name *</Label>
                      <Input id="full_name" data-testid="input-full-name" value={form.full_name} onChange={set("full_name")} placeholder="Your full name" className={inputCls} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company_name" className="text-sm font-medium text-slate-700">Company Name</Label>
                      <Input id="company_name" data-testid="input-company" value={form.company_name} onChange={set("company_name")} placeholder="Company (optional)" className={inputCls} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email Address *</Label>
                      <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={set("email")} placeholder="you@example.co.za" className={inputCls} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-slate-700">Phone Number *</Label>
                      <Input id="phone" data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="0XX XXX XXXX" className={inputCls} required />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="council_account_number" className="text-sm font-medium text-slate-700">Council Account Number</Label>
                      <Input id="council_account_number" data-testid="input-account-number" value={form.council_account_number} onChange={set("council_account_number")} placeholder="If available (optional)" className={inputCls} />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">Service Required *</Label>
                      <Select value={form.service_required} onValueChange={setSel("service_required")}>
                        <SelectTrigger data-testid="select-service" className={inputCls}><SelectValue placeholder="Select a service" /></SelectTrigger>
                        <SelectContent>
                          {FORM_OPTIONS.service.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">Property Type</Label>
                      <Select value={form.property_type} onValueChange={setSel("property_type")}>
                        <SelectTrigger data-testid="select-property" className={inputCls}><SelectValue placeholder="Select property type" /></SelectTrigger>
                        <SelectContent>
                          {FORM_OPTIONS.propertyType.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-sm font-medium text-slate-700">Municipality</Label>
                      <Select value={form.municipality} onValueChange={setSel("municipality")}>
                        <SelectTrigger data-testid="select-municipality" className={inputCls}><SelectValue placeholder="Select municipality" /></SelectTrigger>
                        <SelectContent>
                          {FORM_OPTIONS.municipality.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="message" className="text-sm font-medium text-slate-700">Message / Requirements *</Label>
                      <Textarea id="message" data-testid="input-message" value={form.message} onChange={set("message")} rows={5}
                        placeholder="Please provide a brief description of the municipal account issue or service you require."
                        className="bg-slate-50 border-slate-200 rounded-lg focus-visible:ring-2 focus-visible:ring-secondary/40 focus-visible:border-secondary resize-none" required />
                    </div>

                    <div className="md:col-span-2 pt-2">
                      <button
                        type="submit" disabled={loading} data-testid="quote-submit"
                        className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-white font-semibold rounded-full px-8 py-4 hover:bg-primary/90 disabled:opacity-70 transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_-8px_rgba(15,23,42,0.5)]"
                      >
                        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Request My Quote <Send className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>}
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
